import { ScheduledTestsItemType, type Season } from '@/types/components';
import type { ScheduledTest, TestScheduleDay } from '@/types/scheduledTests';
import { getSeasonForMonth } from '@/utils/date';

export type ScheduledTestsItem =
  | { type: ScheduledTestsItemType.SeasonHeader; season: Season }
  | { type: ScheduledTestsItemType.MonthHeader; date: Date }
  | { type: ScheduledTestsItemType.DayHeader; date: string }
  | { type: ScheduledTestsItemType.Test; test: ScheduledTest }
  | { type: ScheduledTestsItemType.EmptyMonth };

const LAST_SCHOOL_YEAR_MONTH = 8;

const toAbsoluteMonth = (relativeMonth: number): number =>
  relativeMonth % 12 === 0 ? 12 : relativeMonth % 12;

const isThisSchoolYearsMonth = (
  relativeMonth: number,
  startMonth: number,
): boolean => {
  if (startMonth <= LAST_SCHOOL_YEAR_MONTH) {
    return relativeMonth <= LAST_SCHOOL_YEAR_MONTH;
  }

  return relativeMonth <= LAST_SCHOOL_YEAR_MONTH + 12;
};

export const buildScheduledTestsItems = (
  testDays: TestScheduleDay[],
  now: Date,
): ScheduledTestsItem[] => {
  const currentMonth = now.getMonth() + 1;
  const currentYear = now.getFullYear();
  const items: ScheduledTestsItem[] = [];
  let currentSeason = getSeasonForMonth(currentMonth);

  items.push({
    type: ScheduledTestsItemType.SeasonHeader,
    season: currentSeason,
  });

  for (
    let relativeMonth = currentMonth;
    isThisSchoolYearsMonth(relativeMonth, currentMonth);
    relativeMonth += 1
  ) {
    const absoluteMonth = toAbsoluteMonth(relativeMonth);
    const season = getSeasonForMonth(absoluteMonth);

    if (season !== currentSeason) {
      currentSeason = season;
      items.push({ type: ScheduledTestsItemType.SeasonHeader, season });
    }

    const year = relativeMonth > 12 ? currentYear + 1 : currentYear;

    items.push({
      type: ScheduledTestsItemType.MonthHeader,
      date: new Date(year, absoluteMonth - 1, 1, 12),
    });

    const testDaysInMonth = testDays.filter((day) => {
      const dayDate = new Date(day.date);

      return (
        dayDate.getMonth() + 1 === absoluteMonth &&
        dayDate.getFullYear() === year
      );
    });

    if (testDaysInMonth.length === 0) {
      items.push({ type: ScheduledTestsItemType.EmptyMonth });
      continue;
    }

    for (const day of testDaysInMonth) {
      items.push({ type: ScheduledTestsItemType.DayHeader, date: day.date });

      for (const test of day.scheduledTests) {
        items.push({ type: ScheduledTestsItemType.Test, test });
      }
    }
  }

  return items;
};
